import { useEffect, useMemo, useRef, useState } from "react";
import {
  createAppointment,
  reschedulePreview,
  validatePrebookPlan,
} from "../../api/appointments";
import {
  getEligibleStaffBulk,
  getEligibleStaffForService,
} from "../../api/services";
import SBINModal from "./SBINModal";
import {
  LABEL_TEXT,
  FIELD_CLASS,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../ui/modalStyles";
import TimePickerPopover from "../TimePickerPopover";
import { snapTimeStringToQuarterHour } from "../../utils/timeRounding";
import { getPreviewStatusMessage } from "../../utils/bookingMessages";
import { useAuth } from "../../context/AuthContext";
import { getSalonTimezone } from "../../utils/salonTimezone";

function formatDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function parseTimeToMinutes(timeStr) {
  if (!timeStr) return null;
  const trimmed = String(timeStr).trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let hh = parseInt(match[1], 10);
    const mm = parseInt(match[2], 10);
    const ap = match[3].toUpperCase();
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    if (hh === 12) hh = 0;
    if (ap === "PM") hh += 12;
    return hh * 60 + mm;
  }
  const parts = trimmed.split(":");
  if (parts.length < 2) return null;
  const hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
}

function hhmmToMinutes(hhmm) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function addMinutesToLocalDateTime(dateStr, timeStr, minutesToAdd) {
  if (!dateStr || !timeStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = timeStr.split(":").map(Number);
  if (
    [year, month, day, hour, minute].some((value) => Number.isNaN(value))
  ) {
    return null;
  }
  const base = Date.UTC(year, month - 1, day, hour, minute, 0);
  const next = new Date(base + minutesToAdd * 60_000);
  const yyyy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(next.getUTCDate()).padStart(2, "0");
  const hh = String(next.getUTCHours()).padStart(2, "0");
  const min = String(next.getUTCMinutes()).padStart(2, "0");
  return { dateStr: `${yyyy}-${mm}-${dd}`, timeStr: `${hh}:${min}` };
}

function zonedTimeToUtcDate(dateStr, timeStr, timeZone) {
  if (!dateStr || !timeStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = timeStr.split(":").map(Number);
  if (
    [year, month, day, hour, minute].some((value) => Number.isNaN(value))
  ) {
    return null;
  }
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  if (!timeZone) {
    return utcDate;
  }
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(utcDate);
    const lookup = { year: "", month: "", day: "", hour: "", minute: "" };
    for (const part of parts) {
      if (Object.prototype.hasOwnProperty.call(lookup, part.type)) {
        lookup[part.type] = part.value;
      }
    }
    if (
      !lookup.year ||
      !lookup.month ||
      !lookup.day ||
      !lookup.hour ||
      !lookup.minute
    ) {
      return utcDate;
    }
    const zonedTime = Date.UTC(
      Number(lookup.year),
      Number(lookup.month) - 1,
      Number(lookup.day),
      Number(lookup.hour),
      Number(lookup.minute),
      0
    );
    const offsetMs = zonedTime - utcDate.getTime();
    return new Date(utcDate.getTime() - offsetMs);
  } catch (err) {
    return utcDate;
  }
}

function formatUtcDateTimeString(dateObj) {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
    return null;
  }
  const yyyy = dateObj.getUTCFullYear();
  const mm = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dateObj.getUTCDate()).padStart(2, "0");
  const HH = String(dateObj.getUTCHours()).padStart(2, "0");
  const MM = String(dateObj.getUTCMinutes()).padStart(2, "0");
  const SS = "00";
  return `${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}`;
}

function toUtcDateTimeParts(dateStr, timeStr, timeZone) {
  const utcDate = zonedTimeToUtcDate(dateStr, timeStr, timeZone);
  if (!utcDate) return null;
  const yyyy = utcDate.getUTCFullYear();
  const mm = String(utcDate.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(utcDate.getUTCDate()).padStart(2, "0");
  const HH = String(utcDate.getUTCHours()).padStart(2, "0");
  const MM = String(utcDate.getUTCMinutes()).padStart(2, "0");
  return { dateStr: `${yyyy}-${mm}-${dd}`, timeStr: `${HH}:${MM}` };
}

function includesOutsideHoursSignal(value) {
  if (!value) return false;
  const text = String(value).toUpperCase();
  return (
    text.includes("OUTSIDE_SALON_HOURS") ||
    text.includes("SALON_CLOSED") ||
    text.includes("OUTSIDE_BUSINESS_HOURS")
  );
}

function addWeeksToDateStr(dateStr, weeks) {
  const base = dateStr ? new Date(`${dateStr}T00:00:00`) : null;
  if (!base || Number.isNaN(base.getTime())) return dateStr;
  const next = new Date(base.getTime() + weeks * 7 * 24 * 60 * 60 * 1000);
  const yyyy = next.getFullYear();
  const mm = String(next.getMonth() + 1).padStart(2, "0");
  const dd = String(next.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatStaffLabel(staff) {
  if (!staff) return "Technician";
  return (
    staff.display_name ||
    staff.name ||
    `${staff.first_name || ""} ${staff.last_name || ""}`.trim() ||
    `Staff #${staff.id}`
  );
}

function createBookingGroupId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `bg_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getDurationMinutes(appt) {
  const start = appt?.start_time ? new Date(appt.start_time) : null;
  const end = appt?.end_time ? new Date(appt.end_time) : null;
  if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
    const duration = Math.round((end.getTime() - start.getTime()) / 60000);
    if (Number.isFinite(duration) && duration > 0) return duration;
  }
  const serviceDuration = Number(appt?.service?.duration_minutes);
  if (Number.isFinite(serviceDuration) && serviceDuration > 0) {
    return serviceDuration;
  }
  return 60;
}

function normalizeDuration(value, fallback = 60) {
  const num = Number(value);
  if (Number.isFinite(num) && num > 0) return num;
  return fallback;
}

function buildPlanFromAppointments(appts, anchorStart) {
  return (appts || []).map((appt, index) => {
    const staffMember = appt?.staff_member || appt?.staff || null;
    const fallbackStaffLabel = appt?.staff_id ? `Staff #${appt.staff_id}` : "Technician";
    const apptStart = appt?.start_time ? new Date(appt.start_time) : null;
    const offsetMinutes =
      anchorStart && apptStart && !Number.isNaN(apptStart.getTime())
        ? Math.round((apptStart.getTime() - anchorStart.getTime()) / 60000)
        : 0;
    return {
      key: appt?.id ?? `${appt?.service_id || "service"}-${index}`,
      appointment_id: appt?.id ?? null,
      service_id: appt?.service?.id ?? appt?.service_id ?? null,
      service_name:
        appt?.service?.name ||
        appt?.service_name ||
        (appt?.service_id ? `Service #${appt.service_id}` : "Service"),
      duration_minutes: normalizeDuration(getDurationMinutes(appt)),
      offset_minutes: offsetMinutes,
      staff_id:
        appt?.staff_member?.id ??
        appt?.staff_id ??
        appt?.staff?.id ??
        "",
      staff_label: staffMember ? formatStaffLabel(staffMember) : fallbackStaffLabel,
      price_at_booking:
        appt?.total_price ??
        appt?.service?.base_price ??
        0,
    };
  });
}

function buildPlanFromTicketItems(items) {
  return (items || []).map((item, index) => {
    const staffMember = item?.staff || null;
    const fallbackStaffLabel = item?.staff_id ? `Staff #${item.staff_id}` : "Technician";
    return {
      key: item?.appointment_id ?? item?.id ?? `${item?.service_id}-${index}`,
      appointment_id: item?.appointment_id ?? null,
      service_id: item?.service_id ?? null,
      service_name: item?.description || "Service",
      duration_minutes: normalizeDuration(item?.duration_minutes, 60),
      offset_minutes: 0,
      staff_id: item?.staff_id ?? "",
      staff_label: staffMember ? formatStaffLabel(staffMember) : fallbackStaffLabel,
      price_at_booking: item?.unit_price ?? 0,
    };
  });
}

export default function PreBookNextVisitModal({
  isOpen,
  onClose,
  anchorAppointment,
  client,
  staff,
  dateStr,
  slotTime,
  clientAppointmentsToday = [],
  ticketServiceItems = [],
  staffOptions = [],
  salonId,
  onCreated,
}) {
  const [mode, setMode] = useState("single");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [staffId, setStaffId] = useState("");
  const [servicePlan, setServicePlan] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOk, setPreviewOk] = useState(false);
  const [previewReason, setPreviewReason] = useState("");
  const [previewMessage, setPreviewMessage] = useState("");
  const [previewSalonHours, setPreviewSalonHours] = useState(null);
  const [previewConflicts, setPreviewConflicts] = useState([]);
  const [eligibleStaff, setEligibleStaff] = useState([]);
  const [eligibleStaffByService, setEligibleStaffByService] = useState({});
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [planValidation, setPlanValidation] = useState({
    overall_ok: false,
    errors: [],
    items: [],
  });
  const [planValidationLoading, setPlanValidationLoading] = useState(false);
  const [staffLookupLoading, setStaffLookupLoading] = useState({});
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const requestIdRef = useRef(0);
  const { currentSalon } = useAuth();
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );

  const anchor = anchorAppointment;
  const hasGroup = Boolean(anchor?.booking_group_id);

  const anchorStart = useMemo(() => {
    const base = anchor?.start_time ? new Date(anchor.start_time) : null;
    if (!base || Number.isNaN(base.getTime())) return null;
    return base;
  }, [anchor?.start_time]);

  const defaultDateStr = useMemo(() => {
    if (anchorStart) {
      return formatDateInput(
        new Date(anchorStart.getTime() + 21 * 24 * 60 * 60 * 1000)
      );
    }
    if (dateStr) return dateStr;
    return "";
  }, [anchorStart, dateStr]);

  const defaultTimeStr = useMemo(() => {
    if (anchorStart) {
      return snapTimeStringToQuarterHour(formatTimeInput(anchorStart));
    }
    if (slotTime) return snapTimeStringToQuarterHour(slotTime);
    return "";
  }, [anchorStart, slotTime]);

  useEffect(() => {
    if (!isOpen) return;
    setMode(hasGroup ? "group" : "single");
    setSelectedDate(defaultDateStr);
    setSelectedTime(defaultTimeStr);
    const defaultStaff =
      anchor?.staff_member?.id ?? anchor?.staff_id ?? staff?.id ?? "";
    setStaffId(defaultStaff ? String(defaultStaff) : "");
    setPreviewOk(false);
    setPreviewReason("");
    setPreviewMessage("");
    setPreviewSalonHours(null);
    setPreviewConflicts([]);
    setEligibleStaff([]);
    setEligibleLoading(false);
    setEligibleStaffByService({});
    setPlanValidation({ overall_ok: false, errors: [], items: [] });
    setPlanValidationLoading(false);
    setStaffLookupLoading({});
    setServicePlan([]);
    setCreateError("");
    setPreviewLoading(false);
    setCreating(false);
  }, [isOpen, hasGroup, defaultDateStr, defaultTimeStr, anchor, staff]);

  useEffect(() => {
    if (!isOpen || !anchor?.id || mode === "group") return;
    if (!selectedDate || !selectedTime) {
      setPreviewLoading(false);
      setPreviewOk(false);
      setPreviewReason("");
      setPreviewMessage("");
      setPreviewSalonHours(null);
      setPreviewConflicts([]);
      return;
    }
    const snappedTime = snapTimeStringToQuarterHour(selectedTime);
    const utcStart = formatUtcDateTimeString(
      zonedTimeToUtcDate(selectedDate, snappedTime, salonTimezone)
    );
    const startString = utcStart || `${selectedDate}T${snappedTime}:00`;
    if (!startString) return;

    setPreviewLoading(true);
    const currentRequestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      (async () => {
        try {
          const body = {
            mode,
            new_start_time: startString,
            staff_id: staffId ? Number(staffId) : undefined,
            allow_double_book: false,
            allow_past: false,
            allow_locked_anchor: true,
          };
          const res = await reschedulePreview(anchor.id, body);
          if (requestIdRef.current !== currentRequestId) return;
          setPreviewOk(Boolean(res?.ok));
          setPreviewReason(res?.reason || "");
          setPreviewMessage(res?.message || "");
          setPreviewSalonHours(res?.salon_hours || null);
          setPreviewConflicts(Array.isArray(res?.conflicts) ? res.conflicts : []);
        } catch (err) {
          if (requestIdRef.current !== currentRequestId) return;
          const detail = err?.response?.data?.detail;
          const detailMessage =
            typeof detail === "string"
              ? detail
              : detail?.message || "";
          const message = detailMessage || err?.message || "Preview failed.";
          setPreviewOk(false);
          setPreviewReason(detail?.code || detail?.reason || message);
          setPreviewMessage(message);
          setPreviewSalonHours(detail?.salon_hours || detail?.hours || null);
          setPreviewConflicts([]);
        } finally {
          if (requestIdRef.current === currentRequestId) {
            setPreviewLoading(false);
          }
        }
      })();
    }, 300);

    return () => clearTimeout(timer);
  }, [isOpen, anchor?.id, mode, selectedDate, selectedTime, staffId, salonTimezone]);

  useEffect(() => {
    if (!isOpen || mode !== "group") return;
    if (!selectedDate || !selectedTime || !servicePlan.length || !salonId) {
      setPlanValidation({ overall_ok: false, errors: [], items: [] });
      setPlanValidationLoading(false);
      return;
    }
    const snappedTime = snapTimeStringToQuarterHour(selectedTime);
    const utcParts = toUtcDateTimeParts(selectedDate, snappedTime, salonTimezone);
    if (!utcParts) return;

    setPlanValidationLoading(true);
    const currentRequestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      (async () => {
        try {
          const body = {
            salon_id: Number(salonId),
            date: utcParts.dateStr,
            time: utcParts.timeStr,
            service_items: servicePlan.map((row) => ({
              service_id: Number(row.service_id),
              staff_id: row.staff_id ? Number(row.staff_id) : null,
              duration_minutes: Number(row.duration_minutes || 0),
              offset_minutes: Number(row.offset_minutes || 0),
            })),
          };
          const res = await validatePrebookPlan(body);
          if (requestIdRef.current !== currentRequestId) return;
          setPlanValidation(res || { overall_ok: false, errors: [], items: [] });
        } catch (err) {
          if (requestIdRef.current !== currentRequestId) return;
          setPlanValidation({ overall_ok: false, errors: [], items: [] });
        } finally {
          if (requestIdRef.current === currentRequestId) {
            setPlanValidationLoading(false);
          }
        }
      })();
    }, 300);

    return () => clearTimeout(timer);
  }, [
    isOpen,
    mode,
    selectedDate,
    selectedTime,
    servicePlan,
    salonId,
    salonTimezone,
  ]);

  const groupAppointments = useMemo(() => {
    if (!hasGroup || !anchor?.booking_group_id) return [];
    return clientAppointmentsToday.filter(
      (appt) =>
        appt &&
        appt.booking_group_id &&
        appt.booking_group_id === anchor.booking_group_id
    );
  }, [hasGroup, anchor?.booking_group_id, clientAppointmentsToday]);

  const initialServicePlan = useMemo(() => {
    if (!isOpen || mode !== "group") return [];
    if (groupAppointments.length) {
      return buildPlanFromAppointments(groupAppointments, anchorStart);
    }
    if (ticketServiceItems.length) {
      return buildPlanFromTicketItems(ticketServiceItems);
    }
    if (anchor) {
      return buildPlanFromAppointments([anchor], anchorStart);
    }
    return [];
  }, [
    isOpen,
    mode,
    groupAppointments,
    ticketServiceItems,
    anchor,
    anchorStart,
  ]);

  const serviceIdsForPlanKey = useMemo(() => {
    const ids = servicePlan
      .map((row) => row.service_id)
      .filter(Boolean)
      .map((id) => Number(id));
    return Array.from(new Set(ids)).sort((a, b) => a - b).join(",");
  }, [servicePlan]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "group") {
      setServicePlan(initialServicePlan);
    } else {
      setServicePlan([]);
    }
  }, [isOpen, mode, initialServicePlan]);

  const serviceIdsForScope = useMemo(() => {
    if (!anchor) return [];
    const singleServiceId =
      anchor?.service?.id ?? anchor?.service_id ?? null;
    return singleServiceId ? [singleServiceId] : [];
  }, [anchor]);

  useEffect(() => {
    if (!isOpen || mode !== "group") return;
    const serviceIds = serviceIdsForPlanKey
      ? serviceIdsForPlanKey.split(",").map((value) => Number(value))
      : [];
    if (!serviceIds.length) {
      setEligibleStaffByService({});
      return;
    }

    let isActive = true;
    const token = null;
    setEligibleLoading(true);

    (async () => {
      try {
        const data = await getEligibleStaffBulk(serviceIds, token);
        if (!isActive) return;
        setEligibleStaffByService(data || {});
      } catch (err) {
        if (!isActive) return;
        setEligibleStaffByService({});
      } finally {
        if (isActive) {
          setEligibleLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [isOpen, mode, serviceIdsForPlanKey]);

  useEffect(() => {
    if (!isOpen || mode === "group") return;
    if (!serviceIdsForScope.length) {
      setEligibleStaff([]);
      return;
    }

    let isActive = true;
    const token = null;
    setEligibleLoading(true);

    (async () => {
      try {
        const data = await getEligibleStaffBulk(serviceIdsForScope, token);
        if (!isActive) return;
        const staffByService = data || {};
        const staffLists = serviceIdsForScope.map(
          (serviceId) => staffByService[serviceId] || []
        );
        setEligibleStaff(staffLists[0] || []);
      } catch (err) {
        if (!isActive) return;
        setEligibleStaff([]);
      } finally {
        if (isActive) {
          setEligibleLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [isOpen, mode, serviceIdsForScope]);

  useEffect(() => {
    if (!isOpen || mode === "group") return;
    if (!eligibleStaff.length) {
      setStaffId("");
      return;
    }
    const currentId = staffId ? Number(staffId) : null;
    const hasMatch = currentId
      ? eligibleStaff.some((member) => member.id === currentId)
      : false;
    if (!hasMatch) {
      setStaffId(String(eligibleStaff[0].id));
    }
  }, [eligibleStaff, staffId, isOpen, mode]);

  const totalDurationMinutes = useMemo(() => {
    if (mode === "group") {
      return servicePlan.reduce(
        (sum, row) => sum + normalizeDuration(row.duration_minutes),
        0
      );
    }
    return anchor ? getDurationMinutes(anchor) : 0;
  }, [mode, servicePlan, anchor]);

  const localHoursWithin = useMemo(() => {
    if (!selectedTime || !previewSalonHours) return null;
    if (previewSalonHours.is_closed) return false;
    const startMin = parseTimeToMinutes(selectedTime);
    const openMin = hhmmToMinutes(previewSalonHours.open_time);
    const closeMin = hhmmToMinutes(previewSalonHours.close_time);
    if (
      !Number.isFinite(startMin) ||
      !Number.isFinite(openMin) ||
      !Number.isFinite(closeMin)
    ) {
      return null;
    }
    const endMin = startMin + totalDurationMinutes;
    return startMin >= openMin && endMin <= closeMin;
  }, [selectedTime, previewSalonHours, totalDurationMinutes]);

  const previewOutsideHours = useMemo(() => {
    const conflicts = Array.isArray(previewConflicts) ? previewConflicts : [];
    return (
      includesOutsideHoursSignal(previewReason) ||
      includesOutsideHoursSignal(previewMessage) ||
      conflicts.some((conflict) =>
        includesOutsideHoursSignal(conflict?.detail || conflict?.reason)
      )
    );
  }, [previewReason, previewMessage, previewConflicts]);

  const previewOutsideHoursOverride =
    previewOutsideHours && localHoursWithin === true;

  const previewStatus = useMemo(() => {
    if (previewOk || previewLoading) return null;
    if (previewOutsideHoursOverride) return null;
    return getPreviewStatusMessage({
      reason: previewReason,
      message: previewMessage,
      conflicts: previewConflicts,
      salonHours: previewSalonHours,
    });
  }, [
    previewOk,
    previewLoading,
    previewOutsideHoursOverride,
    previewReason,
    previewMessage,
    previewConflicts,
    previewSalonHours,
  ]);

  const planStatusCounts = useMemo(() => {
    const counts = {
      available: 0,
      conflict: 0,
      notEligible: 0,
      outsideHours: 0,
    };
    (planValidation.items || []).forEach((item) => {
      switch ((item?.status || "").toUpperCase()) {
        case "AVAILABLE":
          counts.available += 1;
          break;
        case "CONFLICT":
          counts.conflict += 1;
          break;
        case "NOT_ELIGIBLE":
          counts.notEligible += 1;
          break;
        case "OUTSIDE_HOURS":
          counts.outsideHours += 1;
          break;
        default:
          break;
      }
    });
    return counts;
  }, [planValidation.items]);

  const groupPreviewMessage = useMemo(() => {
    if (planValidationLoading) return "Checking...";
    if (!selectedDate || !selectedTime) return "Select a date and time to preview.";
    if (!servicePlan.length) return "No services found for this booking.";
    if (planStatusCounts.outsideHours > 0) {
      const outside = (planValidation.items || []).find(
        (item) => (item?.status || "").toUpperCase() === "OUTSIDE_HOURS"
      );
      return outside?.message || "This time is outside salon hours.";
    }
    if (planStatusCounts.conflict > 0) {
      const label = planStatusCounts.conflict === 1 ? "service has" : "services have";
      return `${planStatusCounts.conflict} ${label} a conflict. Update the technician(s) highlighted in red.`;
    }
    if (planStatusCounts.notEligible > 0) {
      if (planStatusCounts.notEligible === 1) {
        return "This technician is not assigned to this service. Update service eligibility or choose another technician.";
      }
      return `${planStatusCounts.notEligible} services are not assigned to the selected technicians. Update service eligibility or choose other technicians.`;
    }
    if (planValidation.overall_ok) return "All services available.";
    return "Review service assignments.";
  }, [
    planValidationLoading,
    selectedDate,
    selectedTime,
    servicePlan.length,
    planStatusCounts,
    planValidation.items,
    planValidation.overall_ok,
  ]);

  const getStatusMeta = (status) => {
    const normalized = String(status || "").toUpperCase();
    if (normalized === "AVAILABLE") {
      return { label: "Available", className: "text-emerald-600" };
    }
    if (normalized === "CONFLICT") {
      return { label: "Conflict", className: "text-rose-600" };
    }
    if (normalized === "NOT_ELIGIBLE") {
      return { label: "Not eligible", className: "text-rose-600" };
    }
    if (normalized === "OUTSIDE_HOURS") {
      return { label: "Outside hours", className: "text-rose-600" };
    }
    return { label: "Pending", className: "text-slate-500" };
  };

  const eligibilityWarning =
    mode !== "group" && eligibleStaff.length === 0 && serviceIdsForScope.length > 0
      ? "This service is not assigned to an available technician. Update service eligibility or choose another service."
      : "";

  const updateServicePlanStaff = (index, nextStaffId) => {
    setServicePlan((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, staff_id: nextStaffId } : row
      )
    );
  };

  const handleFindAvailableTechnicians = async (row, index) => {
    if (!row?.service_id || !selectedDate || !selectedTime || !salonId) return;
    const snappedTime = snapTimeStringToQuarterHour(selectedTime);
    const offsetMinutes = Number(row.offset_minutes || 0);
    const rowStart = addMinutesToLocalDateTime(
      selectedDate,
      snappedTime,
      offsetMinutes
    );
    if (!rowStart) return;
    const utcParts = toUtcDateTimeParts(
      rowStart.dateStr,
      rowStart.timeStr,
      salonTimezone
    );
    if (!utcParts) return;
    const params = {
      date: utcParts.dateStr,
      time: utcParts.timeStr,
      duration_minutes: row.duration_minutes,
      salon_id: Number(salonId),
    };
    setStaffLookupLoading((prev) => ({ ...prev, [index]: true }));
    try {
      const token = null;
      const list = await getEligibleStaffForService(
        row.service_id,
        params,
        token
      );
      const sorted = Array.isArray(list)
        ? [...list].sort((a, b) => {
            const aAvailable = a.available === true;
            const bAvailable = b.available === true;
            if (aAvailable === bAvailable) {
              return String(a.display_name || "").localeCompare(
                String(b.display_name || "")
              );
            }
            return aAvailable ? -1 : 1;
          })
        : [];
      setEligibleStaffByService((prev) => ({
        ...prev,
        [row.service_id]: sorted,
      }));
    } catch (err) {
      // ignore lookup errors; keep existing list
    } finally {
      setStaffLookupLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleCreate = async () => {
    if (!anchor || !client?.id || !salonId) return;
    const snappedTime = snapTimeStringToQuarterHour(selectedTime);
    const baseStart = addMinutesToLocalDateTime(selectedDate, snappedTime, 0);
    if (!baseStart) return;

    setCreating(true);
    setCreateError("");
    try {
      const bookingGroupId =
        mode === "group" && hasGroup ? createBookingGroupId() : null;
      const anchorStaffId =
        staffId ? Number(staffId) : anchor?.staff_id ?? staff?.id;

      const targets = mode === "group" ? servicePlan : [anchor];

      for (const entry of targets) {
        const offsetMinutes =
          mode === "group" ? Number(entry.offset_minutes || 0) : 0;
        const durationMinutes =
          mode === "group" ? normalizeDuration(entry.duration_minutes) : getDurationMinutes(entry);
        const localStart = addMinutesToLocalDateTime(
          baseStart.dateStr,
          baseStart.timeStr,
          offsetMinutes
        );
        if (!localStart) {
          throw new Error("Invalid appointment start time.");
        }
        const localEnd = addMinutesToLocalDateTime(
          localStart.dateStr,
          localStart.timeStr,
          durationMinutes
        );
        if (!localEnd) {
          throw new Error("Invalid appointment end time.");
        }
        const startUtc = formatUtcDateTimeString(
          zonedTimeToUtcDate(
            localStart.dateStr,
            localStart.timeStr,
            salonTimezone
          )
        );
        const endUtc = formatUtcDateTimeString(
          zonedTimeToUtcDate(localEnd.dateStr, localEnd.timeStr, salonTimezone)
        );
        const startPayload =
          startUtc || `${localStart.dateStr}T${localStart.timeStr}:00`;
        const endPayload =
          endUtc || `${localEnd.dateStr}T${localEnd.timeStr}:00`;
        const serviceId =
          mode === "group"
            ? entry.service_id
            : entry?.service?.id ?? entry?.service_id ?? anchor?.service?.id ?? anchor?.service_id;
        if (!serviceId) {
          throw new Error("Missing service for appointment.");
        }

        const staffSelection =
          mode === "group"
            ? Number(entry.staff_id || 0)
            : entry?.id === anchor?.id
            ? anchorStaffId
            : entry?.staff_id ?? anchorStaffId;
        if (!staffSelection) {
          throw new Error("Technician is required.");
        }

        await createAppointment(
          {
            salon_id: Number(salonId),
            client_id: client.id,
            staff_id: staffSelection,
            booking_group_id: bookingGroupId,
            start_time: startPayload,
            end_time: endPayload,
            client_type: "by_client_during_service",
            services: [
              {
                service_id: Number(serviceId),
                duration_minutes: durationMinutes,
                price_at_booking:
                  entry?.price_at_booking ??
                  entry?.total_price ??
                  entry?.service?.base_price ??
                  0,
                sort_order: 0,
              },
            ],
          },
          { allowPast: false }
        );
      }
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : detail?.message || err?.message || "Unable to create next visit.";
      setCreateError(message);
    } finally {
      setCreating(false);
    }
  };

  const createDisabled =
    creating ||
    (mode === "group"
      ? planValidationLoading || !planValidation.overall_ok || !servicePlan.length
      : (!previewOk && !previewOutsideHoursOverride) || !eligibleStaff.length);

  if (!isOpen) return null;

  return (
    <SBINModal
      isOpen={isOpen}
      onClose={onClose}
      title="Pre-book next visit"
      size="xxl"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className={BTN_OUTLINE}
            disabled={creating}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={createDisabled}
            className={BTN_PRIMARY}
          >
            {creating ? "Creating..." : "Create next visit"}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {hasGroup && (
          <div className="rounded-2xl border border-slate-200 px-4 py-3">
            <div className={LABEL_TEXT}>Scope</div>
            <div className="mt-2 space-y-2 text-slate-700">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="prebook-mode"
                  value="single"
                  checked={mode === "single"}
                  onChange={() => setMode("single")}
                  className="h-4 w-4 border border-slate-300 text-rose-600 focus:ring-0"
                />
                This service only
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="prebook-mode"
                  value="group"
                  checked={mode === "group"}
                  onChange={() => setMode("group")}
                  className="h-4 w-4 border border-slate-300 text-rose-600 focus:ring-0"
                />
                All services in this booking
              </label>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 10 }, (_, index) => index + 1).map((weeks) => (
            <button
              key={weeks}
              type="button"
              onClick={() =>
                setSelectedDate((prev) => addWeeksToDateStr(prev, weeks))
              }
              className="rounded-full border border-slate-300 px-4 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
            >
              +{weeks} wk
            </button>
          ))}
          <button
            type="button"
            className="rounded-full border border-slate-300 px-4 py-2 text-sm md:text-base font-semibold text-slate-700 hover:bg-slate-50"
            onClick={() => {
              const now = new Date();
              const yyyy = now.getFullYear();
              const mm = String(now.getMonth() + 1).padStart(2, "0");
              const dd = String(now.getDate()).padStart(2, "0");
              setSelectedDate(`${yyyy}-${mm}-${dd}`);
            }}
          >
            Today
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className={LABEL_TEXT}>Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              className={`w-full ${FIELD_CLASS}`}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_TEXT}>Time</label>
            <TimePickerPopover
              value={selectedTime}
              onChange={(next) =>
                setSelectedTime(snapTimeStringToQuarterHour(next))
              }
              inputClassName={FIELD_CLASS}
            />
          </div>
          {mode === "group" ? (
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className={LABEL_TEXT}>Service assignments</label>
              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-[1.4fr,1fr,0.8fr] gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                  <span>Service</span>
                  <span>Technician</span>
                  <span>Status</span>
                </div>
                {servicePlan.length === 0 && (
                  <div className="px-4 py-3 text-sm text-slate-500">
                    No services found for this booking.
                  </div>
                )}
                {servicePlan.map((row, index) => {
                  const validation = planValidation.items?.[index] || null;
                  const statusMeta = getStatusMeta(validation?.status);
                  const statusClass = statusMeta.className;
                  const highlightRow =
                    validation &&
                    ["CONFLICT", "NOT_ELIGIBLE", "OUTSIDE_HOURS"].includes(
                      String(validation?.status || "").toUpperCase()
                    );
                  const staffList = eligibleStaffByService[row.service_id] || [];
                  const hasCurrentInList = staffList.some(
                    (member) => Number(member.id) === Number(row.staff_id)
                  );
                  const showFindAvailable =
                    String(validation?.status || "").toUpperCase() === "CONFLICT";
                  return (
                    <div
                      key={row.key}
                      className={`grid grid-cols-1 md:grid-cols-[1.4fr,1fr,0.8fr] gap-3 px-4 py-3 border-t ${
                        highlightRow ? "border-rose-200 bg-rose-50/30" : "border-slate-100"
                      }`}
                    >
                      <div className="text-sm font-medium text-slate-900">
                        {row.service_name}
                      </div>
                      <div className="space-y-1">
                        <select
                          value={row.staff_id}
                          onChange={(event) =>
                            updateServicePlanStaff(index, event.target.value)
                          }
                          className={`w-full ${FIELD_CLASS}`}
                          disabled={eligibleLoading}
                        >
                          <option value="">Select technician</option>
                          {!hasCurrentInList && row.staff_id ? (
                            <option value={row.staff_id}>
                              {row.staff_label || `Staff #${row.staff_id}`} (not eligible)
                            </option>
                          ) : null}
                          {staffList.map((member) => {
                            const isUnavailable = member.available === false;
                            const label = `${formatStaffLabel(member)}${
                              isUnavailable ? " (not available)" : ""
                            }`;
                            return (
                              <option
                                key={member.id}
                                value={member.id}
                                disabled={isUnavailable}
                              >
                                {label}
                              </option>
                            );
                          })}
                        </select>
                        {showFindAvailable && (
                          <button
                            type="button"
                            onClick={() => handleFindAvailableTechnicians(row, index)}
                            className="text-xs font-semibold text-sky-600 hover:text-sky-700"
                            disabled={staffLookupLoading[index]}
                          >
                            {staffLookupLoading[index]
                              ? "Finding technicians..."
                              : "Find available technicians"}
                          </button>
                        )}
                      </div>
                      <div className="text-sm">
                        <div
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass}`}
                        >
                          {statusMeta.label}
                        </div>
                        {validation?.message ? (
                          <div className="mt-1 text-xs text-slate-500">
                            {validation.message}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1 md:col-span-2">
              <label className={LABEL_TEXT}>Technician</label>
              <select
                value={staffId}
                onChange={(event) => setStaffId(event.target.value)}
                className={`w-full ${FIELD_CLASS}`}
                disabled={eligibleLoading || !eligibleStaff.length}
              >
                {!eligibleStaff.length && (
                  <option value="">No eligible technicians</option>
                )}
                {eligibleStaff.map((member) => (
                  <option key={member.id} value={member.id}>
                    {formatStaffLabel(member)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 px-4 py-3">
          <div className={LABEL_TEXT}>Preview</div>
          <div className="mt-2 text-slate-700">
            {eligibilityWarning && (
              <div className="mb-2 text-rose-600">{eligibilityWarning}</div>
            )}
            {mode === "group" ? (
              <div
                className={
                  planValidation.overall_ok && !planValidationLoading
                    ? "text-emerald-600 font-semibold"
                    : planValidationLoading
                    ? "text-slate-600"
                    : "text-rose-600"
                }
              >
                {groupPreviewMessage}
              </div>
            ) : (
              <>
                {previewLoading && <span>Checking...</span>}
                {!previewLoading && previewOk && (
                  <span className="text-emerald-600">Available</span>
                )}
                {!previewLoading && !previewOk && previewStatus && (
                  <div className="text-rose-600">
                    <div className="font-semibold">{previewStatus.title}</div>
                    <div className="mt-1 text-slate-600">
                      {previewStatus.message}
                    </div>
                    {previewStatus.hint && (
                      <div className="mt-2 text-slate-500 text-sm">
                        {previewStatus.hint}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {createError && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
            {createError}
          </div>
        )}
      </div>
    </SBINModal>
  );
}
